/**
 * Audit Log Tab - טאב יומן ביקורת
 * View and export audit logs for compliance
 */

import { useEffect, useState } from 'react'
import { useAdminStore } from '../../store/adminStore'
import { RefreshCw, Download, Filter, Calendar } from 'lucide-react'

export default function AuditLogTab() {
  const {
    auditLogs,
    auditLogsTotal,
    auditLogsLoading,
    auditLogsError,
    auditLogFilters,
    auditLogsPagination,
    auditStats,
    fetchAuditLogs,
    exportAuditLogs,
    fetchAuditStats,
    setAuditLogFilters,
    setAuditLogsPagination,
    clearAuditLogsError
  } = useAdminStore()

  const [showFilters, setShowFilters] = useState(false)
  const [showExportModal, setShowExportModal] = useState(false)
  const [exportStartDate, setExportStartDate] = useState('')
  const [exportEndDate, setExportEndDate] = useState('')
  const [exporting, setExporting] = useState(false)
  const [selectedTimeRange, setSelectedTimeRange] = useState<string>('all')

  // Format current date for datetime-local input (YYYY-MM-DDTHH:mm)
  const formatDateTimeLocal = (date: Date) => {
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    const hours = String(date.getHours()).padStart(2, '0')
    const minutes = String(date.getMinutes()).padStart(2, '0')
    return `${year}-${month}-${day}T${hours}:${minutes}`
  }

  // Initialize with current date/time
  const [customStartDate, setCustomStartDate] = useState(() => {
    const now = new Date()
    now.setHours(0, 0, 0, 0) // Start of day
    return formatDateTimeLocal(now)
  })
  const [customEndDate, setCustomEndDate] = useState(() => {
    const now = new Date()
    return formatDateTimeLocal(now)
  })

  useEffect(() => {
    fetchAuditLogs()
    fetchAuditStats()
  }, [auditLogFilters, auditLogsPagination.page])

  // Handle time range filter changes
  const handleTimeRangeChange = (range: string) => {
    setSelectedTimeRange(range)

    const now = new Date()
    let startDate: Date | undefined
    let endDate: Date | undefined = now

    switch (range) {
      case 'today':
        startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0)
        endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999)
        break
      case '3days':
        startDate = new Date(now.getTime() - 3 * 24 * 60 * 60 * 1000)
        break
      case 'week':
        startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000)
        break
      case 'month':
        startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000)
        break
      case 'all':
        startDate = undefined
        endDate = undefined
        break
      case 'custom':
        // Don't apply filter yet, wait for user to select dates
        return
    }

    setAuditLogFilters({
      startDate: startDate?.toISOString(),
      endDate: endDate?.toISOString()
    })
  }

  // Apply custom date range
  const handleApplyCustomRange = () => {
    if (!customStartDate || !customEndDate) {
      alert('נא לבחור טווח תאריכים')
      return
    }

    const start = new Date(customStartDate)
    const end = new Date(customEndDate)

    if (start > end) {
      alert('תאריך התחלה חייב להיות לפני תאריך סיום')
      return
    }

    setAuditLogFilters({
      startDate: start.toISOString(),
      endDate: end.toISOString()
    })
  }

  const handleExport = async () => {
    if (!exportStartDate || !exportEndDate) {
      alert('נא לבחור טווח תאריכים')
      return
    }

    setExporting(true)
    try {
      await exportAuditLogs({
        startDate: exportStartDate,
        endDate: exportEndDate,
        filters: auditLogFilters,
        format: 'csv'
      })
      setShowExportModal(false)
    } catch (error) {
      console.error('Export error:', error)
    } finally {
      setExporting(false)
    }
  }

  const handlePageChange = (newPage: number) => {
    setAuditLogsPagination({ page: newPage })
  }

  const getRiskLevelBadge = (level: string) => {
    switch (level) {
      case 'low':
        return <span className="badge badge-success text-xs">נמוך</span>
      case 'medium':
        return <span className="badge badge-info text-xs">בינוני</span>
      case 'high':
        return <span className="badge badge-warning text-xs">גבוה</span>
      case 'critical':
        return <span className="badge badge-error text-xs">קריטי</span>
      default:
        return <span className="badge text-xs">{level}</span>
    }
  }

  const getSuccessBadge = (success: boolean) => {
    return success ? (
      <span className="badge badge-success text-xs">הצליח</span>
    ) : (
      <span className="badge badge-error text-xs">נכשל</span>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-heading font-bold">יומן ביקורת</h2>
          <p className="text-gray-600 mt-1">
            מעקב אחר כל הפעולות במערכת לתאימות ובטיחות
          </p>
        </div>

        <div className="flex gap-2">
          <button
            onClick={() => setShowExportModal(true)}
            className="btn btn-outline"
          >
            <Download size={18} aria-hidden="true" />
            ייצוא CSV
          </button>
          <button
            onClick={() => fetchAuditLogs()}
            disabled={auditLogsLoading}
            className="btn btn-outline"
          >
            <RefreshCw
              size={18}
              className={auditLogsLoading ? 'animate-spin' : ''}
              aria-hidden="true"
            />
            רענון
          </button>
        </div>
      </div>

      {/* Statistics */}
      {auditStats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="card bg-blue-50 border-blue-200">
            <p className="text-sm text-gray-600 mb-1">סה״כ לוגים</p>
            <p className="text-3xl font-bold text-blue-700">
              {auditStats.totalLogs.toLocaleString()}
            </p>
          </div>
          <div className="card bg-green-50 border-green-200">
            <p className="text-sm text-gray-600 mb-1">שיעור הצלחה</p>
            <p className="text-3xl font-bold text-green-700">
              {auditStats.successRate}%
            </p>
          </div>
          <div className="card bg-yellow-50 border-yellow-200">
            <p className="text-sm text-gray-600 mb-1">פעילות 24 שעות</p>
            <p className="text-3xl font-bold text-yellow-700">
              {auditStats.recentActivity}
            </p>
          </div>
          <div className="card bg-red-50 border-red-200">
            <p className="text-sm text-gray-600 mb-1">כשלונות</p>
            <p className="text-3xl font-bold text-red-700">
              {auditStats.failureCount}
            </p>
          </div>
        </div>
      )}

      {/* Time Range Filters */}
      <div className="card">
        <div className="flex items-center gap-2 mb-3">
          <Calendar size={18} className="text-gray-600" aria-hidden="true" />
          <h3 className="font-semibold text-gray-900">טווח זמן</h3>
        </div>

        {/* Quick Time Filters */}
        <div className="flex flex-wrap gap-2 mb-4">
          <button
            onClick={() => handleTimeRangeChange('all')}
            className={`btn btn-sm ${
              selectedTimeRange === 'all' ? 'btn-primary' : 'btn-outline'
            }`}
          >
            הכל
          </button>
          <button
            onClick={() => handleTimeRangeChange('today')}
            className={`btn btn-sm ${
              selectedTimeRange === 'today' ? 'btn-primary' : 'btn-outline'
            }`}
          >
            היום
          </button>
          <button
            onClick={() => handleTimeRangeChange('3days')}
            className={`btn btn-sm ${
              selectedTimeRange === '3days' ? 'btn-primary' : 'btn-outline'
            }`}
          >
            3 ימים
          </button>
          <button
            onClick={() => handleTimeRangeChange('week')}
            className={`btn btn-sm ${
              selectedTimeRange === 'week' ? 'btn-primary' : 'btn-outline'
            }`}
          >
            שבוע
          </button>
          <button
            onClick={() => handleTimeRangeChange('month')}
            className={`btn btn-sm ${
              selectedTimeRange === 'month' ? 'btn-primary' : 'btn-outline'
            }`}
          >
            חודש
          </button>
          <button
            onClick={() => {
              setSelectedTimeRange('custom')
            }}
            className={`btn btn-sm ${
              selectedTimeRange === 'custom' ? 'btn-primary' : 'btn-outline'
            }`}
          >
            מותאם אישית
          </button>
        </div>

        {/* Custom Date Range Picker */}
        {selectedTimeRange === 'custom' && (
          <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <label htmlFor="audit-custom-start" className="block text-sm font-medium mb-1">תאריך התחלה</label>
                <input
                  id="audit-custom-start"
                  type="datetime-local"
                  value={customStartDate}
                  onChange={(e) => setCustomStartDate(e.target.value)}
                  className="input text-sm w-full"
                />
              </div>
              <div>
                <label htmlFor="audit-custom-end" className="block text-sm font-medium mb-1">תאריך סיום</label>
                <input
                  id="audit-custom-end"
                  type="datetime-local"
                  value={customEndDate}
                  onChange={(e) => setCustomEndDate(e.target.value)}
                  className="input text-sm w-full"
                />
              </div>
              <div className="flex items-end">
                <button
                  onClick={handleApplyCustomRange}
                  className="btn btn-primary btn-sm w-full"
                >
                  החל טווח
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Active Date Range Display */}
        {auditLogFilters.startDate && auditLogFilters.endDate && (
          <div className="mt-3 text-sm text-gray-600 bg-blue-50 p-2 rounded border border-blue-200">
            <span className="font-medium">טווח פעיל:</span>{' '}
            {new Date(auditLogFilters.startDate).toLocaleString('he-IL')} -{' '}
            {new Date(auditLogFilters.endDate).toLocaleString('he-IL')}
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="flex gap-2">
        <button
          onClick={() => setShowFilters(!showFilters)}
          className="btn btn-outline"
        >
          <Filter size={18} aria-hidden="true" />
          סינון מתקדם
        </button>
      </div>

      {showFilters && (
        <div className="card bg-gray-50">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Action Filter */}
            <div>
              <label htmlFor="audit-filter-action" className="block text-sm font-medium mb-1">פעולה</label>
              <input
                id="audit-filter-action"
                type="text"
                placeholder="LOGIN, USER_CREATE..."
                value={auditLogFilters.action || ''}
                onChange={(e) => setAuditLogFilters({ action: e.target.value || undefined })}
                className="input text-sm"
              />
            </div>

            {/* Risk Level Filter */}
            <div>
              <label htmlFor="audit-filter-risk" className="block text-sm font-medium mb-1">רמת סיכון</label>
              <select
                id="audit-filter-risk"
                value={auditLogFilters.riskLevel || ''}
                onChange={(e) =>
                  setAuditLogFilters({ riskLevel: e.target.value as any || undefined })
                }
                className="input text-sm"
              >
                <option value="">הכל</option>
                <option value="low">נמוך</option>
                <option value="medium">בינוני</option>
                <option value="high">גבוה</option>
                <option value="critical">קריטי</option>
              </select>
            </div>

            {/* Success Filter */}
            <div>
              <label htmlFor="audit-filter-success" className="block text-sm font-medium mb-1">תוצאה</label>
              <select
                id="audit-filter-success"
                value={
                  auditLogFilters.success === undefined
                    ? ''
                    : auditLogFilters.success
                    ? 'true'
                    : 'false'
                }
                onChange={(e) =>
                  setAuditLogFilters({
                    success: e.target.value === '' ? undefined : e.target.value === 'true'
                  })
                }
                className="input text-sm"
              >
                <option value="">הכל</option>
                <option value="true">הצליח</option>
                <option value="false">נכשל</option>
              </select>
            </div>

            {/* Compliance Flag */}
            <div>
              <label htmlFor="audit-filter-compliance" className="block text-sm font-medium mb-1">דגל תאימות</label>
              <select
                id="audit-filter-compliance"
                value={auditLogFilters.complianceFlag || ''}
                onChange={(e) =>
                  setAuditLogFilters({
                    complianceFlag: e.target.value as any || undefined
                  })
                }
                className="input text-sm"
              >
                <option value="">הכל</option>
                <option value="ISA">ISA</option>
                <option value="GDPR">GDPR</option>
                <option value="PII_ACCESS">גישה ל-PII</option>
                <option value="ADMIN_ACTION">פעולת מנהל</option>
              </select>
            </div>
          </div>

          <div className="mt-4">
            <button
              onClick={() => setAuditLogFilters({})}
              className="btn btn-sm btn-outline"
            >
              נקה סינון
            </button>
          </div>
        </div>
      )}

      {/* Error Alert */}
      {auditLogsError && (
        <div className="alert alert-error" role="alert">
          <p>{auditLogsError}</p>
          <button onClick={clearAuditLogsError} className="btn btn-sm btn-outline">
            סגור
          </button>
        </div>
      )}

      {/* Audit Logs Table */}
      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-2 text-right font-medium text-gray-700">תאריך</th>
                <th className="px-3 py-2 text-right font-medium text-gray-700">פעולה</th>
                <th className="px-3 py-2 text-right font-medium text-gray-700">משאב</th>
                <th className="px-3 py-2 text-right font-medium text-gray-700">תוצאה</th>
                <th className="px-3 py-2 text-right font-medium text-gray-700">רמת סיכון</th>
                <th className="px-3 py-2 text-right font-medium text-gray-700">IP</th>
                <th className="px-3 py-2 text-right font-medium text-gray-700">דגלים</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {auditLogsLoading ? (
                <tr>
                  <td colSpan={7} className="px-3 py-6 text-center text-gray-500">
                    טוען לוגים...
                  </td>
                </tr>
              ) : auditLogs.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-3 py-6 text-center text-gray-500">
                    לא נמצאו לוגים
                  </td>
                </tr>
              ) : (
                auditLogs.map((log) => (
                  <tr key={log._id} className="hover:bg-gray-50">
                    <td className="px-3 py-2 text-gray-900 whitespace-nowrap">
                      {new Date(log.createdAt).toLocaleString('he-IL', {
                        day: '2-digit',
                        month: '2-digit',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </td>
                    <td className="px-3 py-2 text-gray-700 font-mono text-xs">
                      {log.action}
                    </td>
                    <td className="px-3 py-2 text-gray-600 font-mono text-xs">
                      {log.resourceType && (
                        <span>
                          {log.resourceType}
                          {log.resourceId && `:${log.resourceId.substring(0, 8)}...`}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2">{getSuccessBadge(log.success)}</td>
                    <td className="px-3 py-2">{getRiskLevelBadge(log.riskLevel)}</td>
                    <td className="px-3 py-2 text-gray-600 font-mono text-xs">
                      {log.ipAddress}
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex flex-wrap gap-1">
                        {log.complianceFlags.slice(0, 2).map((flag, idx) => (
                          <span key={idx} className="badge badge-gray text-xs">
                            {flag}
                          </span>
                        ))}
                        {log.complianceFlags.length > 2 && (
                          <span className="text-xs text-gray-500">
                            +{log.complianceFlags.length - 2}
                          </span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {auditLogsTotal > auditLogsPagination.limit && (
          <div className="border-t px-4 py-3 flex items-center justify-between">
            <p className="text-sm text-gray-700">
              מציג {auditLogs.length} מתוך {auditLogsTotal.toLocaleString()} לוגים
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => handlePageChange(auditLogsPagination.page - 1)}
                disabled={auditLogsPagination.page === 1}
                className="btn btn-sm btn-outline"
              >
                הקודם
              </button>
              <span className="px-3 py-1 text-sm">עמוד {auditLogsPagination.page}</span>
              <button
                onClick={() => handlePageChange(auditLogsPagination.page + 1)}
                disabled={auditLogs.length < auditLogsPagination.limit}
                className="btn btn-sm btn-outline"
              >
                הבא
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Export Modal */}
      {showExportModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full">
            <div className="border-b px-6 py-4">
              <h2 className="text-xl font-bold">ייצוא לוגי ביקורת</h2>
            </div>

            <div className="p-6 space-y-4">
              <div className="alert alert-info text-sm">
                <Calendar size={18} aria-hidden="true" />
                <p>בחר טווח תאריכים לייצוא (מקסימום שנה)</p>
              </div>

              <div>
                <label htmlFor="audit-export-start" className="block text-sm font-medium mb-2">תאריך התחלה</label>
                <input
                  id="audit-export-start"
                  type="date"
                  value={exportStartDate}
                  onChange={(e) => setExportStartDate(e.target.value)}
                  className="input"
                  required
                />
              </div>

              <div>
                <label htmlFor="audit-export-end" className="block text-sm font-medium mb-2">תאריך סיום</label>
                <input
                  id="audit-export-end"
                  type="date"
                  value={exportEndDate}
                  onChange={(e) => setExportEndDate(e.target.value)}
                  className="input"
                  required
                />
              </div>

              <p className="text-xs text-gray-500">
                הייצוא יכלול את כל הסינונים הפעילים בטבלה
              </p>
            </div>

            <div className="border-t px-6 py-4 flex gap-3 justify-end">
              <button
                onClick={() => setShowExportModal(false)}
                disabled={exporting}
                className="btn btn-outline"
              >
                ביטול
              </button>
              <button
                onClick={handleExport}
                disabled={exporting}
                className="btn btn-primary"
              >
                {exporting ? 'מייצא...' : 'ייצא CSV'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Compliance Note */}
      <div className="alert alert-info text-sm">
        <p>
          💡 <strong>הערה:</strong> יומני ביקורת נשמרים למשך 7 שנים לתאימות עם
          דרישות רשות ני״ע ו-GDPR.
        </p>
      </div>
    </div>
  )
}
